import config from '../config.js';

interface GeminiConfig {
  apiKey: string;
  model: string;
  systemPrompt: string;
  contactPhone: string;
  contactEmail: string;
  contactLocation: string;
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  image?: {
    data: string;
    mimeType: string;
  };
}

interface GeminiResponse {
  candidates: Array<{
    content: {
      parts: Array<{
        text: string;
      }>;
    };
  }>;
}

interface ChatbotSettingsResponse {
  success: boolean;
  data: {
    gemini_api_key: string;
    gemini_model: string;
    system_prompt: string;
    contact_phone: string;
    contact_email: string;
    contact_location: string;
  };
}

class GeminiService {
  private config: GeminiConfig | null = null;
  private configPromise: Promise<GeminiConfig> | null = null;
  
  constructor() {
    // Configuration will be loaded dynamically from database
  }

  private async loadConfig(): Promise<GeminiConfig> {
    if (this.config) {
      return this.config;
    }

    if (this.configPromise) {
      return this.configPromise;
    }

    this.configPromise = this.fetchConfigFromDatabase();
    this.config = await this.configPromise;
    return this.config;
  }

  private async fetchConfigFromDatabase(): Promise<GeminiConfig> {
    try {
      const response = await fetch(`${config.apiBaseUrl}/public-chatbot-settings.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ChatbotSettingsResponse = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error('Invalid response format from chatbot settings API');
      }

      return {
        apiKey: data.data.gemini_api_key || '',
        model: data.data.gemini_model || 'gemini-2.0-flash',
        systemPrompt: data.data.system_prompt || 'You are a helpful AI assistant.',
        contactPhone: data.data.contact_phone || '+91 7905333017',
        contactEmail: data.data.contact_email || 'contact@drgurjar.com',
        contactLocation: data.data.contact_location || 'Rajasthan, India'
      };
    } catch (error) {
      console.error('Failed to load chatbot configuration from database:', error);
      
      // Fallback configuration
      return {
        apiKey: '',
        model: 'gemini-2.0-flash',
        systemPrompt: 'You are a helpful AI assistant. I\'m currently unable to load my full configuration, but I\'ll do my best to help you.',
        contactPhone: '+91 7905333017',
        contactEmail: 'contact@drgurjar.com',
        contactLocation: 'Rajasthan, India'
      };
    }
  }

  async sendMessage(userMessage: string, imageData?: { data: string; mimeType: string }): Promise<string> {
    try {
      // Load configuration from database
      const config = await this.loadConfig();
      
      if (!config.apiKey) {
        return "I'm currently unable to connect to the AI service. Please contact Dr. Ramhari directly at +91 7905333017 for immediate assistance.";
      }

      // Prepare parts array with system prompt and user message
      let parts: any[] = [{
        text: `${config.systemPrompt}\n\nUser Question: ${userMessage}`
      }];
      
      // Add image data if present
      if (imageData) {
        parts.push({
          inline_data: {
            mime_type: imageData.mimeType,
            data: imageData.data
          }
        });
      }

      // Prepare the request payload
      const requestBody = {
        contents: [{
          parts: parts
        }],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 1024,
        }
      };

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${config.model}:generateContent?key=${config.apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: GeminiResponse = await response.json();
      
      if (data.candidates && data.candidates.length > 0) {
        return data.candidates[0].content.parts[0].text;
      } else {
        throw new Error('No response generated');
      }
    } catch (error) {
      console.error('Gemini API Error:', error);
      
      // Provide fallback responses based on message content
      return await this.getFallbackResponse(userMessage);
    }
  }

  private async getFallbackResponse(userMessage: string): Promise<string> {
    const message = userMessage.toLowerCase();
    const config = await this.loadConfig();
    
    if (message.includes('consultation') || message.includes('appointment') || message.includes('book')) {
      return `I'd be happy to help you book a consultation with Dr. Ramhari! 📅\n\nYou can reach him directly at:\n• Phone: ${config.contactPhone}\n• Email: ${config.contactEmail}\n\nHe offers emergency consultations, routine health checks, farm visits, and phone consultations. What type of consultation do you need?`;
    }
    
    if (message.includes('service') || message.includes('what') || message.includes('help')) {
      return "Dr. Ramhari offers comprehensive veterinary services: 🩺\n\n• Health consultation & diagnostics\n• Farm management solutions\n• Expert training programs\n• Nutrition planning\n• Biosecurity solutions\n• Laboratory services\n\nWhich service interests you most?";
    }
    
    if (message.includes('contact') || message.includes('phone') || message.includes('email')) {
      return `Here's how to reach Dr. Ramhari: 📞\n\n• Phone: ${config.contactPhone}\n• Email: ${config.contactEmail}\n• Location: ${config.contactLocation}\n\nHe's available for consultations and can provide immediate assistance for urgent matters. How can he help you today?`;
    }
    
    if (message.includes('emergency') || message.includes('urgent') || message.includes('sick')) {
      return `For urgent veterinary matters, please contact Dr. Ramhari immediately: 🚨\n\n📞 Phone: ${config.contactPhone}\n\nHe provides emergency consultations and can guide you through immediate care steps. Don't delay if your birds are showing signs of illness!`;
    }
    
    if (message.includes('health') || message.includes('disease') || message.includes('prevention')) {
      return "Dr. Ramhari specializes in poultry health management! 🐔\n\nKey areas include:\n• Disease prevention & biosecurity\n• Nutrition optimization\n• Health monitoring protocols\n• Treatment planning\n\nFor specific health concerns, I recommend scheduling a consultation. What health topic interests you most?";
    }
    
    // Default fallback
    return `Thank you for your message! While I'm having trouble connecting to my AI service right now, I can still help you with basic information about Dr. Ramhari's services. 🤖\n\nFor immediate assistance, please contact:\n📞 ${config.contactPhone}\n✉️ ${config.contactEmail}\n\nWhat would you like to know more about?`;
  }

  clearHistory(): void {
    // No conversation history to clear in stateless approach
  }

  getConversationHistory(): ChatMessage[] {
    return []; // No conversation history in stateless approach
  }
}

// Export singleton instance
export const geminiService = new GeminiService();
export default geminiService;